// src/services/promotionService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class PromotionService {
  /**
   * Get all promotions
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllPromotions(params = {}) {
    try {
      console.log('🎉 Fetching all promotions with params:', params)
      
      const response = await ApiService.get(AppURLs.PROMOTIONS.GET_ALL, params)
      
      console.log('✅ Promotions fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching promotions:', error)
      throw error
    }
  }

  /**
   * Get promotion by ID
   * @param {string} id - Promotion ID
   * @returns {Promise<Object>} - API response
   */
  async getPromotionById(id) {
    try {
      console.log('🎉 Fetching promotion by ID:', id)
      
      if (!id) {
        throw new Error('Promotion ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PROMOTIONS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      
      console.log('✅ Promotion fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching promotion:', error)
      throw error
    }
  }

  /**
   * Create new promotion
   * @param {Object} promotionData - Promotion data
   * @returns {Promise<Object>} - API response
   */
  async createPromotion(promotionData) {
    try {
      console.log('🎉 Creating promotion with data:', promotionData)
      
      // Validate required fields
      this.validatePromotionData(promotionData)
      
      const response = await ApiService.post(AppURLs.PROMOTIONS.CREATE, promotionData)
      
      console.log('✅ Promotion created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating promotion:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        const conflictError = new Error(error.response.data?.message || 'Promotion already exists')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  /**
   * Update promotion using PATCH
   * @param {string} id - Promotion ID
   * @param {Object} promotionData - Updated promotion data
   * @returns {Promise<Object>} - API response
   */
  async updatePromotion(id, promotionData) {
    try {
      console.log('🎉 Updating promotion:', { id, promotionData })
      
      if (!id) {
        throw new Error('Promotion ID is required')
      }

      // Validate partial data for updates
      this.validatePartialPromotionData(promotionData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.PROMOTIONS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      console.log('📡 Full URL:', `${AppURLs.API}${endpoint}`)
      
      // Use PATCH for updates
      const response = await ApiService.patch(endpoint, promotionData)
      console.log('✅ Promotion updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating promotion:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        const conflictError = new Error(error.response.data?.message || 'Promotion data conflicts with existing record')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 404) {
        const notFoundError = new Error('Promotion not found')
        notFoundError.response = error.response
        throw notFoundError
      }
      
      throw error
    }
  }

  /**
   * Delete promotion
   * @param {string} id - Promotion ID
   * @returns {Promise<Object>} - API response
   */
  async deletePromotion(id) {
    try {
      console.log('🎉 Deleting promotion:', id)
      
      if (!id) {
        throw new Error('Promotion ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PROMOTIONS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      
      console.log('✅ Promotion deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting promotion:', error)
      throw error
    }
  }

  /**
   * Get active promotions
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - API response
   */
  async getActivePromotions(params = {}) {
    try {
      console.log('🎉 Fetching active promotions with params:', params)
      
      const response = await ApiService.get(AppURLs.PROMOTIONS.GET_ALL, params)
      
      console.log('✅ Active promotions fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching active promotions:', error)
      throw error
    }
  }

  /**
   * Get promotions by type
   * @param {string} promotionType - Type of promotion (product_based, order_based, etc.)
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} - API response
   */
  async getPromotionsByType(promotionType, params = {}) {
    try {
      console.log('🎉 Fetching promotions by type:', promotionType, 'with params:', params)
      
      if (!promotionType) {
        throw new Error('Promotion type is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PROMOTIONS.BY_TYPE, { promotionType })
      const response = await ApiService.get(endpoint, params)
      
      console.log('✅ Promotions by type fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching promotions by type:', error)
      throw error
    }
  }

  /**
   * Get promotions statistics
   * @returns {Promise<Object>} - API response
   */
  async getPromotionStats() {
    try {
      console.log('🎉 Fetching promotion statistics')
      
      const response = await ApiService.get(AppURLs.PROMOTIONS.STATS)
      
      console.log('✅ Promotion stats fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching promotion stats:', error)
      throw error
    }
  }

  /**
   * Search promotions
   * @param {string} query - Search query
   * @param {Object} params - Additional search parameters
   * @returns {Promise<Object>} - API response
   */
  async searchPromotions(query, params = {}) {
    try {
      console.log('🎉 Searching promotions with query:', query, 'and params:', params)
      
      const searchParams = { ...params, q: query }
      const response = await ApiService.get(AppURLs.PROMOTIONS.SEARCH, searchParams)
      
      console.log('✅ Promotion search completed successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching promotions:', error)
      throw error
    }
  }

  /**
   * Toggle promotion status
   * @param {string} id - Promotion ID
   * @param {boolean} isActive - New status
   * @returns {Promise<Object>} - API response
   */
  async togglePromotionStatus(id, isActive) {
    try {
      console.log('🎉 Toggling promotion status:', { id, isActive })
      
      if (!id) {
        throw new Error('Promotion ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.PROMOTIONS.UPDATE, { id })
      const response = await ApiService.patch(endpoint, { isActive })
      
      console.log('✅ Promotion status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling promotion status:', error)
      throw error
    }
  }

  /**
   * Validate promotion data for creation
   * @param {Object} promotionData - Promotion data to validate
   * @throws {Error} - Validation error
   */
  validatePromotionData(promotionData) {
    const required = ['name', 'promotionType', 'discountType', 'discountValue', 'targetAudience', 'startDate', 'endDate']
    
    for (const field of required) {
      if (!promotionData[field]) {
        throw new Error(`${field} is required`)
      }
    }

    // Validate promotion type
    const validTypes = ['product_based', 'order_based', 'customer_based', 'quantity_based']
    if (!validTypes.includes(promotionData.promotionType)) {
      throw new Error('Invalid promotion type')
    }

    // Validate discount type
    const validDiscountTypes = ['fixed', 'percentage']
    if (!validDiscountTypes.includes(promotionData.discountType)) {
      throw new Error('Invalid discount type')
    }

    // Validate discount value
    if (typeof promotionData.discountValue !== 'number' || promotionData.discountValue <= 0) {
      throw new Error('Discount value must be a positive number')
    }

    // Validate percentage discount
    if (promotionData.discountType === 'percentage' && promotionData.discountValue > 100) {
      throw new Error('Percentage discount cannot exceed 100%')
    }

    // Validate priority
    if (promotionData.priority && ![1, 2, 3, 4].includes(promotionData.priority)) {
      throw new Error('Priority must be 1, 2, 3, or 4')
    }

    // Validate dates
    const startDate = new Date(promotionData.startDate)
    const endDate = new Date(promotionData.endDate)
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error('Invalid date format')
    }

    if (endDate <= startDate) {
      throw new Error('End date must be after start date')
    }

    // Validate products for product_based promotions
    if (promotionData.promotionType === 'product_based') {
      if (!promotionData.products || !Array.isArray(promotionData.products) || promotionData.products.length === 0) {
        throw new Error('At least one product is required for product-based promotions')
      }
    }

    // Validate conditions
    if (promotionData.conditions && Array.isArray(promotionData.conditions)) {
      promotionData.conditions.forEach((condition, index) => {
        if (!condition.type) {
          throw new Error(`Condition ${index + 1}: type is required`)
        }
        
        this.validateCondition(condition, index + 1)
      })
    }
  }

  /**
   * Validate partial promotion data for updates
   * @param {Object} promotionData - Partial promotion data to validate
   * @throws {Error} - Validation error
   */
  validatePartialPromotionData(promotionData) {
    // Only validate fields that are provided
    if (promotionData.promotionType) {
      const validTypes = ['product_based', 'order_based', 'customer_based', 'quantity_based']
      if (!validTypes.includes(promotionData.promotionType)) {
        throw new Error('Invalid promotion type')
      }
    }

    if (promotionData.discountType) {
      const validDiscountTypes = ['fixed', 'percentage']
      if (!validDiscountTypes.includes(promotionData.discountType)) {
        throw new Error('Invalid discount type')
      }
    }

    if (promotionData.discountValue !== undefined) {
      if (typeof promotionData.discountValue !== 'number' || promotionData.discountValue <= 0) {
        throw new Error('Discount value must be a positive number')
      }

      if (promotionData.discountType === 'percentage' && promotionData.discountValue > 100) {
        throw new Error('Percentage discount cannot exceed 100%')
      }
    }

    if (promotionData.priority !== undefined) {
      if (![1, 2, 3, 4].includes(promotionData.priority)) {
        throw new Error('Priority must be 1, 2, 3, or 4')
      }
    }

    if (promotionData.startDate && promotionData.endDate) {
      const startDate = new Date(promotionData.startDate)
      const endDate = new Date(promotionData.endDate)
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Invalid date format')
      }

      if (endDate <= startDate) {
        throw new Error('End date must be after start date')
      }
    }

    if (promotionData.conditions && Array.isArray(promotionData.conditions)) {
      promotionData.conditions.forEach((condition, index) => {
        if (!condition.type) {
          throw new Error(`Condition ${index + 1}: type is required`)
        }
        
        this.validateCondition(condition, index + 1)
      })
    }
  }

  /**
   * Validate individual condition
   * @param {Object} condition - Condition to validate
   * @param {number} index - Condition index for error messages
   * @throws {Error} - Validation error
   */
  validateCondition(condition, index) {
    switch (condition.type) {
      case 'days_active':
        if (!condition.daysActive || !Array.isArray(condition.daysActive) || condition.daysActive.length === 0) {
          throw new Error(`Condition ${index}: daysActive array is required`)
        }
        const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        const invalidDays = condition.daysActive.filter(day => !validDays.includes(day))
        if (invalidDays.length > 0) {
          throw new Error(`Condition ${index}: Invalid days: ${invalidDays.join(', ')}`)
        }
        break

      case 'time_range':
        if (!condition.timeRange || !condition.timeRange.startTime || !condition.timeRange.endTime) {
          throw new Error(`Condition ${index}: timeRange with startTime and endTime is required`)
        }
        break

      case 'min_quantity':
        if (typeof condition.minQuantity !== 'number' || condition.minQuantity <= 0) {
          throw new Error(`Condition ${index}: minQuantity must be a positive number`)
        }
        break

      case 'max_uses_per_order':
        if (typeof condition.maxUsesPerOrder !== 'number' || condition.maxUsesPerOrder <= 0) {
          throw new Error(`Condition ${index}: maxUsesPerOrder must be a positive number`)
        }
        break

      case 'min_order_value':
        if (typeof condition.minOrderValue !== 'number' || condition.minOrderValue <= 0) {
          throw new Error(`Condition ${index}: minOrderValue must be a positive number`)
        }
        break

      case 'payment_method':
        if (!condition.paymentMethods || !Array.isArray(condition.paymentMethods) || condition.paymentMethods.length === 0) {
          throw new Error(`Condition ${index}: paymentMethods array is required`)
        }
        const validMethods = ['cash', 'card', 'cheque']
        const invalidMethods = condition.paymentMethods.filter(method => !validMethods.includes(method.toLowerCase()))
        if (invalidMethods.length > 0) {
          throw new Error(`Condition ${index}: Invalid payment methods: ${invalidMethods.join(', ')}`)
        }
        break

      default:
        throw new Error(`Condition ${index}: Invalid condition type: ${condition.type}`)
    }
  }
}

export default new PromotionService()